import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { FEATURE_MANAGEMENT } from "api/ApiPath";
import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { CapabilityInjection } from "features/capabilityManagement/di/CapabilityInjection";
import { CapabilityActionRequestEntity } from "features/capabilityManagement/domain/entities/CapabilityActionRequestEntity";
import { CapabilityDetailEntity } from "features/capabilityManagement/domain/entities/CapabilityDetailEntity";
import { CapabilityRequestEntity } from "features/capabilityManagement/domain/entities/CapabilityRequestEntity";
import { FeatureFilterRequestEntity } from "features/capabilityManagement/domain/entities/filters/FeatureFilterRequestEntity";
import { RefObject } from "react";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { ICapabilityObservedState } from "../interfaces/ICapabilityManagement";

class CapabilityInput implements Disposable {
  public fetchList = new BehaviorSubject<CapabilityRequestEntity>({});
  public fetchDetail = new BehaviorSubject<CapabilityRequestEntity>({});
  public fetchMenu = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchFeatures = new BehaviorSubject<FeatureFilterRequestEntity>({});
  public fetchActions = new BehaviorSubject<{
    action?: "create" | "update" | "delete";
    value?: CapabilityActionRequestEntity;
    detailInfo?: CapabilityDetailEntity;
  }>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchMenu.complete();
    this.fetchFeatures.complete();
    this.fetchActions.complete();
  }
}

class CapabilityOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onActionSuccess = new BehaviorSubject<string>("");
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onActionSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

@injectable()
export class CapabilityViewModel extends BasedObservedViewModel<
  CapabilityInput,
  CapabilityOutput,
  ICapabilityObservedState
> {
  private capabilityUseCase = CapabilityInjection.getUseCase();
  private filterUseCase = FilterInjection.getUseCase();

  protected override createObservedState(): ICapabilityObservedState {
    return {
      loading: false,
      list: [],
      detail: {},
      menus: [],
      features: [],
      paging: {},
    };
  }

  constructor() {
    super(new CapabilityInput(), new CapabilityOutput());
  }

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: RefObject<FormInstance<any>>,
    prevParams: CapabilityRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : "createdAt DESC";

    const query = {
      ...prevParams,
      ...rest,
      created: created ?? prevParams?.created,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({
          loading: true,
          list: []
        });
        const res = await this.capabilityUseCase.getList(params);
        if (res?.data) {
          this.setObservedState({
            list: res.data,
            paging: res.pagination,
          });
        }
      } catch (error) {
        console.error(error);
        this.setObservedState({
          list: [],
          paging: {},
        });
        this.output.onError.next(error);
      } finally {
        this.setObservedState({
          loading: false,
        });
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.setObservedState({
          loading: true,
        });
        const res = await this.capabilityUseCase.getCapabilityDetail(params);
        if (res?.data) {
          this.setObservedState({
            detail: res.data,
          });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({
          loading: false,
        });
      }
    });

    this.skipFirstInitial(this.input.fetchFeatures, async (params) => {
      try {
        const res = await this.filterUseCase.fetchListWithFeature({
          path: FEATURE_MANAGEMENT.USER_FEATURE,
          isForceRefresh: false,
          params,
          savedKey: CacheKeyEnum.internalFeature,
        });
        if (res) {
          this.setObservedState({
            features: res,
          });
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchMenu, async (params) => {
      try {
        const res = await this.capabilityUseCase.getMenu(params);
        if (res?.data) {
          this.setObservedState({
            menus: res.data,
          });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (params) => {
      try {
        this.setObservedState({
          loading: true,
        });

        if (params?.action === "create" && params?.value) {
          const res = await this.capabilityUseCase.createCapability(
            params.value,
            params?.detailInfo,
          );
          if (res?.data) {
            this.output.onActionSuccess.next("create");
          }
        }

        if (params?.action === "update" && params?.value) {
          const res = await this.capabilityUseCase.updateCapability(
            params.value,
            params?.detailInfo,
          );
          if (res?.data) {
            this.output.onActionSuccess.next("update");
          }
        }

        if (params?.action === "delete" && params?.value?.code) {
          const res = await this.capabilityUseCase.deleteCapability({
            code: params.value.code,
          });
          if (res?.data) {
            this.output.onActionSuccess.next("delete");
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({
          loading: false,
        });
      }
    });
  }
}
